// src/components/AccountsPayable.jsx
import React, { useState, useEffect, useMemo } from "react";
import api from "../api/api";
import { Link, useNavigate } from "react-router-dom";
import "../styles/AccountsPayable.css";

const UNLOCK_CODE = "1970";

const AccountsPayable = () => {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();

  // 🔒 Estados del candado (Resumen)
  const [summaryUnlocked, setSummaryUnlocked] = useState(false);
  const [codeInput, setCodeInput] = useState("");
  const [codeError, setCodeError] = useState("");

  // Formatea montos a ₡150.000,00
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("es-CR", {
      style: "currency",
      currency: "CRC",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number(amount || 0));
  };

  const fetchSuppliers = async () => {
    try {
      setLoading(true);

      // ✅ endpoint liviano: id, name, outstandingBalance
      const res = await api.get("/api/suppliers");
      setSuppliers(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error("Error al obtener la lista de proveedores:", error);

      // fallback por si no lo maneja el interceptor
      if (error?.response?.status === 401 || error?.response?.status === 403) {
        localStorage.removeItem("token");
        navigate("/login");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSuppliers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filtrado local
  const filteredSuppliers = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return suppliers;

    return suppliers.filter((supplier) =>
      (supplier?.name || "").toLowerCase().includes(q)
    );
  }, [suppliers, searchQuery]);

  // Total pendiente (sobre lo filtrado)
  const totalOutstanding = useMemo(() => {
    return filteredSuppliers.reduce(
      (acc, sup) => acc + Number(sup?.outstandingBalance || 0),
      0
    );
  }, [filteredSuppliers]);

  const handleSearch = () => {
    // El filtrado ya es automático (esto queda por UX)
    console.log("Buscando:", searchQuery);
  };

  // 🔓 Desbloquear resumen
  const handleUnlock = () => {
    if (codeInput.trim() === UNLOCK_CODE) {
      setSummaryUnlocked(true);
      setCodeInput("");
      setCodeError("");
    } else {
      setCodeError("Código incorrecto");
    }
  };

  // 🔒 Bloquear resumen
  const handleLock = () => {
    setSummaryUnlocked(false);
    setCodeInput("");
    setCodeError("");
  };

  // Enter para desbloquear
  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleUnlock();
  };

  return (
    <div className="accounts-payable-container">
      <h1>Cuentas por Pagar</h1>

      <div className="ap-search-bar">
        <input
          type="text"
          className="ap-search-input"
          placeholder="Buscar proveedor..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />

        <button className="ap-search-button" onClick={handleSearch}>
          Buscar
        </button>

        <button
          className="ap-add-button"
          onClick={() => navigate("/supplier-management")}
        >
          Registrar Proveedor
        </button>
      </div>

      {loading ? (
        <p className="loading-text">Cargando proveedores...</p>
      ) : filteredSuppliers.length === 0 ? (
        <p className="no-accounts-text">No hay proveedores registrados.</p>
      ) : (
        <>
          <table className="ap-table">
            <thead>
              <tr>
                <th>Proveedor</th>
                <th>Saldo Pendiente</th>
                <th>Acción</th>
              </tr>
            </thead>

            <tbody>
              {filteredSuppliers.map((supplier) => (
                <tr key={supplier.id}>
                  <td>{supplier.name}</td>
                  <td>{formatCurrency(supplier.outstandingBalance || 0)}</td>
                  <td>
                    <Link to={`/AccountsPayable/${supplier.id}`}>
                      <button className="ap-action-button">Ver Cuenta</button>
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* =====================
              RESUMEN DE SALDOS
          ===================== */}
          <div className="ap-summary">
            <h2>Resumen de Saldos</h2>

            {!summaryUnlocked ? (
              <div className="ap-lock">
                <div className="ap-lock-icon" aria-hidden="true">
                  🔒
                </div>

                <input
                  type="text"
                  placeholder="Ingrese código"
                  value={codeInput}
                  onChange={(e) => {
                    setCodeInput(e.target.value);
                    if (codeError) setCodeError("");
                  }}
                  onKeyDown={handleKeyDown}
                  autoComplete="off"
                  name="ap_unlock_code"
                  spellCheck={false}
                />

                <button onClick={handleUnlock}>Desbloquear</button>

                {codeError && <div className="ap-lock-error">{codeError}</div>}
              </div>
            ) : (
              <>
                <p>
                  Total de Proveedores:{" "}
                  <strong>{filteredSuppliers.length}</strong>
                </p>
                <p>
                  Monto Total Pendiente:{" "}
                  <strong>{formatCurrency(totalOutstanding)}</strong>
                </p>

                <button className="ap-lock-btn" onClick={handleLock}>
                  🔒 Bloquear resumen
                </button>
              </>
            )}
          </div>
          {/* =====================
              FIN RESUMEN
          ===================== */}
        </>
      )}
    </div>
  );
};

export default AccountsPayable;
